import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  getDocs,
  orderBy,
  query,
  writeBatch,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IGenericError,
  IHistoryItemRaw,
  THistoryMessage,
  THistoryMessageDoc,
} from '../types'
import { QuerySnapshot } from 'firebase-admin/firestore'

const historiesCollectionName = 'histories'
const conversationSubCollectionName = 'conversation'

const historiesCollectionRef = collection(firebaseDb, historiesCollectionName)

const historiesByUserIdCollectionRef = (userId: string) =>
  collection(firebaseDb, historiesCollectionName, userId, 'items')

const historiesConversationCollectionRef = (
  userId: string,
  historyItemId: string,
) =>
  collection(
    firebaseDb,
    historiesCollectionName,
    userId,
    'items',
    historyItemId,
    conversationSubCollectionName,
  )

const historiesDocRef = (id: string) =>
  doc(firebaseDb, historiesCollectionName, id)

export async function updateLessonHistoryItem(
  userId: string,
  documentId: string,
  updateObj: IHistoryItemRaw,
) {
  try {
    const collectionRef = historiesByUserIdCollectionRef(userId)

    const documentRef = doc(collectionRef, documentId)

    await setDoc(documentRef, updateObj, { merge: true })
  } catch (error: unknown) {
    console.error(
      `error while trying to update lesson history item, userId: ${userId}, documentId: ${documentId}. :>> `,
      (error as IGenericError)?.message || error,
      error,
      updateObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update lesson history item`,
    )
  }
}

export async function deleteLessonHistoryItem(
  userId: string,
  documentId: string,
) {
  try {
    const collectionRef = historiesByUserIdCollectionRef(userId)

    const documentRef = doc(collectionRef, documentId)

    const batch = writeBatch(firebaseDb)

    batch.delete(documentRef)

    const subCollectionRef = collection(
      documentRef,
      conversationSubCollectionName,
    )
    const subCollectionDocs = await getDocs(subCollectionRef)

    for (const docSnapshot of subCollectionDocs.docs) {
      batch.delete(docSnapshot.ref)
    }

    await batch.commit()
  } catch (error: unknown) {
    console.error(
      `error while trying to delete lesson history item, userId: ${userId}, documentId: ${documentId}. :>> `,
      (error as IGenericError)?.message || error,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete lesson history item`,
    )
  }
}

export async function saveMessageToHistoryConversation(
  userId: string,
  historyItemId: string,
  message: THistoryMessage,
): Promise<string> {
  try {
    const collectionRef = historiesConversationCollectionRef(
      userId,
      historyItemId,
    )

    const documentRef = await addDoc(collectionRef, {
      ...message,
      createdAt: serverTimestamp(),
    })

    return documentRef.id
  } catch (error: unknown) {
    console.error(
      `error while trying to save message to lesson history conversation sub collection, userId: ${userId}, documentId: ${historyItemId}. :>> `,
      (error as IGenericError)?.message || error,
      error,
      message,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to save message to lesson history`,
    )
  }
}

export async function updateHistoryConversationMessage(
  userId: string,
  historyItemId: string,
  messageId: string,
  updateObj: Partial<THistoryMessage>,
) {
  try {
    const collectionRef = historiesConversationCollectionRef(
      userId,
      historyItemId,
    )

    await updateDoc(doc(collectionRef, messageId), {
      ...updateObj,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while trying to update message in lesson history conversation sub collection, userId: ${userId}, documentId: ${historyItemId}, messageId: ${messageId}. :>> `,
      (error as IGenericError)?.message || error,
      error,
      updateObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update message in lesson history`,
    )
  }
}

export async function getHistoryConversationMessages(
  userId: string,
  historyItemId: string,
): Promise<THistoryMessageDoc[]> {
  try {
    const collectionRef = historiesConversationCollectionRef(
      userId,
      historyItemId,
    )

    const querySnapshot = (await getDocs(
      query(collectionRef, orderBy('createdAt', 'asc')),
    )) as unknown as QuerySnapshot<THistoryMessage>

    const messages: THistoryMessageDoc[] = []

    querySnapshot.docs.forEach((doc) => {
      const message: THistoryMessageDoc = {
        id: doc.id,
        ...doc.data(),
      }
      messages.push(message)
    })

    return messages
  } catch (error: unknown) {
    console.error(
      `error while trying to get messages from lesson history conversation sub collection, userId: ${userId}, documentId: ${historyItemId}. :>> `,
      (error as IGenericError)?.message || error,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get messages from lesson history`,
    )
  }
}
